// WishlistPanel — slide-in right-drawer showing everything the visitor has
// heart-saved. Anonymous localStorage-backed via the useWishlist hook.
// Rendered by the Navigation component; opens when the visitor taps the
// wishlist count badge in the nav.

import { useEffect } from 'react';
import { X, Heart, ArrowUpRight, Trash2 } from 'lucide-react';
import useWishlist from '@/hooks/useWishlist';
import { WISHLIST } from '@/constants/testIds';

export default function WishlistPanel({ open, onClose }) {
  const { items, remove, clear } = useWishlist();

  // Close on Escape and lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return (
    <>
      {/* Scrim */}
      <div
        aria-hidden={!open}
        onClick={onClose}
        className={`fixed inset-0 z-[60] bg-[var(--brand-forest-deep)]/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Panel */}
      <aside
        data-testid={WISHLIST.panel}
        aria-hidden={!open}
        aria-label="Saved items"
        className={`fixed top-0 right-0 z-[61] h-full w-full max-w-md bg-[var(--brand-parchment)] shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <header className="flex items-center justify-between px-6 py-5 border-b border-[var(--brand-stone)]">
          <div className="flex items-center gap-3">
            <Heart size={18} className="text-[var(--brand-gold)] fill-[var(--brand-gold)]" />
            <div>
              <h2 className="font-display text-xl text-[var(--brand-forest)] leading-tight">
                Your saved kit
              </h2>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--brand-muted)]">
                {items.length === 0 ? 'nothing yet' : `${items.length} item${items.length === 1 ? '' : 's'}`}
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid={WISHLIST.panelClose}
            aria-label="Close saved items panel"
            onClick={onClose}
            className="w-9 h-9 grid place-items-center border border-[var(--brand-forest)] text-[var(--brand-forest)] hover:bg-[var(--brand-forest)] hover:text-[var(--brand-parchment)] transition-colors"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {items.length === 0 ? (
            <div
              data-testid={WISHLIST.panelEmpty}
              className="text-sm text-[var(--brand-muted)] italic leading-relaxed"
            >
              Tap the wee brass heart on any product to save it here. Anonymous — no sign-up needed,
              nothing leaves your device unless you enquire.
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((it) => (
                <li
                  key={it.slug}
                  data-testid={WISHLIST.panelItem(it.slug)}
                  className="flex gap-4 border border-[var(--brand-stone)] bg-[var(--brand-surface)] p-3"
                >
                  {it.image && (
                    <img
                      src={it.image}
                      alt=""
                      className="w-20 h-20 object-cover shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-base text-[var(--brand-forest)] leading-snug">
                      {it.name}
                    </p>
                    {it.price && (
                      <p className="text-xs font-semibold text-[var(--brand-olive)] mt-0.5">
                        {it.price}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-3">
                      <a
                        href={`/#${it.slug}`}
                        onClick={onClose}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--brand-forest)] hover:text-[var(--brand-olive)]"
                      >
                        View <ArrowUpRight size={11} />
                      </a>
                      <button
                        type="button"
                        data-testid={WISHLIST.panelRemove(it.slug)}
                        onClick={() => remove(it.slug)}
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold text-[var(--brand-muted)] hover:text-[var(--brand-forest)]"
                      >
                        <Trash2 size={11} /> Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <footer className="border-t border-[var(--brand-stone)] px-6 py-4 flex items-center justify-between">
            <button
              type="button"
              onClick={() => clear()}
              className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[var(--brand-muted)] hover:text-[var(--brand-forest)]"
            >
              Clear all
            </button>
            <a
              href="/#contact"
              onClick={onClose}
              className="btn-gold px-4 py-2 text-xs font-semibold tracking-wide inline-flex items-center gap-1.5"
            >
              Enquire about these
            </a>
          </footer>
        )}
      </aside>
    </>
  );
}
